import {
  type FC,
  useMemo,
  useState,
} from 'react';

import {
  useNavigate,
} from 'react-router-dom';

import AppLayout from '@/components/feature/AppLayout';

import {
  EmptyState,
  LoadingState,
} from '@/components/base';

import {
  useAttentionCenter,
  type AttentionItem,
} from '@/hooks/useAttentionCenter';

const sourceLabel:
  Record<
    AttentionItem['source'],
    string
  > = {
  exception:
    'Exceção',

  deadline:
    'Prazo',

  task:
    'Tarefa',

  communication:
    'Comunicação',

  lead:
    'Lead',

  financial:
    'Financeiro',
};

const severityLabel:
  Record<
    AttentionItem['severity'],
    string
  > = {
  critical:
    'Crítico',

  important:
    'Importante',

  normal:
    'Acompanhar',
};

const severityClass:
  Record<
    AttentionItem['severity'],
    string
  > = {
  critical:
    'bg-red-50 text-red-700 border-red-200',

  important:
    'bg-amber-50 text-amber-700 border-amber-200',

  normal:
    'bg-blue-50 text-blue-700 border-blue-200',
};

const formatDateTime = (
  value:
    string |
    null
) => {
  if (!value) {
    return '—';
  }

  const parsed =
    new Date(
      value
    );

  if (
    Number.isNaN(
      parsed.getTime()
    )
  ) {
    return value;
  }

  return parsed
    .toLocaleString(
      'pt-BR',
      {
        day:
          '2-digit',

        month:
          '2-digit',

        year:
          'numeric',

        hour:
          '2-digit',

        minute:
          '2-digit',
      }
    );
};

const AttentionPage:
  FC = () => {
  const navigate =
    useNavigate();

  const {
    items,
    summary,
    aggregation,
    generatedAt,
    loading,
    error,
    load,
  } =
    useAttentionCenter({
      limit:
        100,

    });

  const [
    source,
    setSource,
  ] =
    useState<
      'all' |
      AttentionItem['source']
    >(
      'all'
    );

  const filtered =
    useMemo(
      () =>
        source ===
          'all'
          ? items
          : items.filter(
              (
                item
              ) =>
                item.source ===
                source
            ),
      [
        items,
        source,
      ]
    );

  return (
    <AppLayout>
      <div className="p-4 md:p-6 space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold text-foreground-900">
              Central de Atenção
            </h2>

            <p className="text-sm text-foreground-500 mt-0.5">
              Pendências reais que exigem decisão, prazo, contato ou acompanhamento
            </p>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs text-foreground-400">
              {generatedAt
                ? `Atualizado em ${formatDateTime(generatedAt)}`
                : 'Atualizando...'}
            </span>

            <button
              type="button"
              onClick={() => void load()}
              className="px-3 py-2 rounded-lg border border-background-200 text-xs font-semibold text-foreground-600 hover:bg-background-100 transition-colors"
            >
              <i className="ri-refresh-line mr-1"></i>
              Atualizar
            </button>
          </div>
        </div>

        {error && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            {error}
          </div>
        )}

        {!aggregation.complete && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            {aggregation.warning ?? 'Não foi possível consolidar todas as fontes. Os dados exibidos podem estar parciais.'}
          </div>
        )}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="rounded-xl border border-background-200 bg-white p-4">
            <p className="text-xs text-foreground-500">Total</p>
            <p className="text-2xl font-bold text-foreground-900 mt-1">
              {summary.total}
            </p>
          </div>

          <div className="rounded-xl border border-red-200 bg-red-50 p-4">
            <p className="text-xs text-red-600">Críticos</p>
            <p className="text-2xl font-bold text-red-700 mt-1">
              {summary.critical}
            </p>
          </div>

          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
            <p className="text-xs text-amber-600">Importantes</p>
            <p className="text-2xl font-bold text-amber-700 mt-1">
              {summary.important}
            </p>
          </div>

          <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
            <p className="text-xs text-blue-600">Acompanhar</p>
            <p className="text-2xl font-bold text-blue-700 mt-1">
              {summary.normal}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {[
            ['all', 'Tudo'],
            ['exception', 'Exceções'],
            ['deadline', 'Prazos'],
            ['task', 'Tarefas'],
            ['communication', 'Comunicações'],
            ['lead', 'Leads'],
            ['financial', 'Financeiro'],
          ].map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setSource(value as typeof source)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                source === value
                  ? 'bg-primary-500 text-white border-primary-500'
                  : 'bg-white text-foreground-600 border-background-200 hover:bg-background-100'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {loading && items.length === 0 ? (
          <LoadingState message="Carregando Central de Atenção..." />
        ) : filtered.length === 0 && aggregation.complete ? (
          <EmptyState
            icon="ri-checkbox-circle-line"
            title="Nenhuma pendência nesta visão"
            description="Não há itens reais de atenção para o filtro selecionado."
          />
        ) : filtered.length === 0 ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm text-amber-800">A consolidação está incompleta; não é possível afirmar que não há pendências.</div>
        ) : (
          <div className="space-y-2">
            {filtered.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => navigate(item.href)}
                className="w-full text-left bg-white border border-background-200 rounded-xl p-4 hover:border-primary-200 hover:bg-primary-50/20 transition-colors"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold ${severityClass[item.severity]}`}>
                        {severityLabel[item.severity]}
                      </span>

                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-background-100 text-foreground-500">
                        {sourceLabel[item.source]}
                      </span>

                      {item.riskLevel && (
                        <span className="text-[10px] text-foreground-400">
                          {item.riskLevel}
                        </span>
                      )}
                    </div>

                    <p className="text-sm font-semibold text-foreground-900">
                      {item.title}
                    </p>

                    {item.detail && (
                      <p className="text-xs text-foreground-500 mt-1 line-clamp-2">
                        {item.detail}
                      </p>
                    )}
                  </div>

                  <div className="text-right flex-shrink-0">
                    <p className="text-[11px] text-foreground-400">
                      {item.dueAt
                        ? formatDateTime(item.dueAt)
                        : formatDateTime(item.occurredAt)}
                    </p>

                    <span className="inline-flex items-center gap-1 text-xs text-primary-600 font-medium mt-2">
                      Abrir
                      <i className="ri-arrow-right-line"></i>
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default AttentionPage;
